import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2, FileText, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function streamPost(path, body, onToken) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.replace(/^data: /, "").trim();
      if (!line) continue;
      try {
        const j = JSON.parse(line);
        if (j.t) onToken(j.t);
      } catch { /* ignore */ }
    }
  }
}

const SESSION = `ht-${Math.random().toString(36).slice(2)}`;

export default function AIAssistant({ apiDate }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "👋 I'm Hat Trick AI. Ask me about tonight's slate, a matchup, or tap a quick action below. I only use the dashboard's real model + odds data." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const run = async (path, body, userLabel) => {
    if (busy) return;
    setBusy(true);
    if (userLabel) setMsgs((m) => [...m, { role: "user", text: userLabel }]);
    setMsgs((m) => [...m, { role: "assistant", text: "" }]);
    try {
      await streamPost(path, { ...body, date: apiDate }, (t) => {
        setMsgs((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + t };
          return c;
        });
      });
    } catch (e) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "⚠️ AI unavailable right now." }; return c; });
    } finally { setBusy(false); }
  };

  const send = () => {
    const q = input.trim();
    if (!q) return;
    setInput("");
    run("/ai/chat", { session_id: SESSION, message: q }, q);
  };

  return (
    <>
      <button
        data-testid="ai-assistant-fab"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-600/90 px-4 py-3 font-mono text-[11px] font-bold uppercase tracking-wider text-white shadow-lg shadow-cyan-500/30 backdrop-blur transition hover:bg-cyan-500"
      >
        <Sparkles className="h-4 w-4" /> AI
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setOpen(false)} data-testid="ai-drawer">
          <div className="flex h-full w-full max-w-md flex-col border-l border-slate-800 bg-slate-950" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-cyan-300"><Sparkles className="h-4 w-4" /> Hat Trick AI</h3>
              <button onClick={() => setOpen(false)} data-testid="ai-close" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex gap-2 border-b border-slate-800 px-4 py-2">
              <button data-testid="ai-briefing" disabled={busy} onClick={() => run("/ai/generate", { kind: "briefing" }, "📋 Daily slate briefing")}
                className="flex items-center gap-1.5 rounded-lg border border-amber-700/50 bg-amber-950/30 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-300 disabled:opacity-40 hover:bg-amber-950/60">
                <FileText className="h-3.5 w-3.5" /> Briefing
              </button>
              <button data-testid="ai-analyze" disabled={busy} onClick={() => run("/ai/generate", { kind: "analyze" }, "🎯 Analyze best bets")}
                className="flex items-center gap-1.5 rounded-lg border border-lime-700/50 bg-lime-950/30 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-lime-300 disabled:opacity-40 hover:bg-lime-950/60">
                <Target className="h-3.5 w-3.5" /> Analyze Best Bets
              </button>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3" data-testid="ai-messages">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] whitespace-pre-wrap rounded-xl px-3 py-2 text-[12px] leading-relaxed ${m.role === "user" ? "bg-cyan-600 text-white" : "border border-slate-800 bg-slate-900/60 text-slate-200"}`}>
                    {m.text || <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="flex items-center gap-2 border-t border-slate-800 p-3">
              <input
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about tonight's slate…"
                className="flex-1 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 font-mono text-[12px] text-slate-100 placeholder:text-slate-600 focus:border-cyan-600 focus:outline-none"
              />
              <button data-testid="ai-send" disabled={busy || !input.trim()} onClick={send} className="rounded-lg bg-cyan-600 p-2 text-white disabled:opacity-40 hover:bg-cyan-500">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
